import React, { useEffect, useRef, useState } from 'react';
import { ToastProps, ToastTone } from '../../../types/DesignSystem/Toast';
import { Cross } from '../Iconography/Icons';
import { TONE_GLYPH } from '../toneGlyph';
import { MOTION } from '../motion';
import { usePresence } from '../usePresence';
import { FOCUS_RING } from '../focusRing';
import { dismissToast, removeToast } from './toastStore';

/**
 * Tone → glyph and ink.
 *
 * **The surface stays white for every tone, and only the glyph is coloured.** That is
 * a deliberate departure from the reference kit this component was specced from,
 * where each tone tinted its own ground and then put the tone colour on top of it —
 * `color-mix(in srgb, var(--success) 16%, var(--background))` with `var(--success)`
 * as the text. Measured, those pairings land at **2.68–4.18:1**: five of them fail AA,
 * and `color-mix()` is exactly why nobody noticed. You cannot eyeball the ratio of a
 * computed tint.
 *
 * On a white ground the body text is a fixed `grey-900` at **14.10:1** and the only
 * thing left to check is the glyph, which needs 3:1 as non-text (SC 1.4.11). All five
 * clear AA, with room:
 *
 *   neutral 4.52 · info 8.53 · success 4.53 · warning 7.42 · danger 6.71
 *
 * Note what the numbers rule out. `green-success-500` — the obvious "success green" —
 * is **2.66:1** and fails even the non-text bar, and `yellow-warning-700` is also
 * 2.66:1. `palette.ts`'s `ROLE` map already flags both (`"FILL ONLY, never text"`),
 * which is why the success ink here is `700` and the warning ink is `950`.
 *
 * Each tone also gets its own SHAPE. Colour alone would breach SC 1.4.1, and it is
 * the difference that matters most at a glance in peripheral vision.
 */
const toneInk: Record<ToastTone, string> = {
  neutral: 'tw-text-tone-neutral-glyph',
  info: 'tw-text-tone-info-glyph',
  success: 'tw-text-tone-success-glyph',
  warning: 'tw-text-tone-warning-glyph',
  danger: 'tw-text-tone-danger-glyph',
};

/**
 * § 297 — the draining hairline wears the toast's TONE, not the accent. A success toast
 * drained navy under a mint glyph and a warning toast drained navy under an amber one —
 * the one part of the surface that moved was the one part that said nothing about what
 * had happened. The bar takes the glyph step, which is measured ≥3:1 on the surface (SC
 * 1.4.11) in both themes; `danger` is in the map for completeness but sticky toasts
 * render no bar (`duration: null`).
 */
const toneBar: Record<ToastTone, string> = {
  neutral: 'tw-bg-tone-neutral-glyph',
  info: 'tw-bg-tone-info-glyph',
  success: 'tw-bg-tone-success-glyph',
  warning: 'tw-bg-tone-warning-glyph',
  danger: 'tw-bg-tone-danger-glyph',
};

/**
 * One toast.
 *
 * Rendered only by `ToastRegion`, from the store — a consumer raises toasts with
 * `toast()` and never mounts this directly.
 *
 * **This element carries no live region.** Announcement is decoupled: `ToastRegion`
 * keeps two always-mounted `sr-only` announcers and copies the text into the right
 * one. Two reasons, and the first is the one that bites:
 *
 * 1. A live region has to be **in the DOM before** the text lands in it. Inserting an
 *    element that is itself `role="status"` is unreliable across screen readers —
 *    some announce it, some see a new subtree and say nothing.
 * 2. This toast holds focusable controls. A live region that also contains buttons
 *    gets re-announced as the user tabs through it.
 *
 * So the visible toast is a plain `<li>`, and the hidden announcers do the talking.
 */
const Toast = React.forwardRef<HTMLLIElement, ToastProps>(function Toast({ toast, ...rest }, ref) {
  const {
    id, tone, title, description, duration, action, open,
    dismissLabel = 'Dismiss notification',
  } = toast;

  const { isPresent, phase } = usePresence(open, MOTION.toast.exitMs);
  const [paused, setPaused] = useState(false);
  /** What is LEFT of the timer, so a pause resumes rather than restarts. */
  const remaining = useRef<number>(duration ?? 0);

  /* The exit animation has finished (or was skipped under reduced motion, which
     `usePresence` handles) — now the record can go. Doing this here rather than in
     `dismissToast` is what gives the toast something to animate out. */
  useEffect(() => {
    if (!isPresent) removeToast(id);
  }, [isPresent, id]);

  /**
   * Auto-dismiss, pausable.
   *
   * The cleanup DECREMENTS `remaining`, which is what makes pause work: when
   * `paused` flips true the effect tears down and books the elapsed time, and when
   * it flips back the effect re-runs with the shorter remainder. Restarting the
   * full duration on every hover would mean a toast you are reading never leaves —
   * and worse, one you brush past resets to five more seconds.
   *
   * Pausing on hover AND focus is what makes an auto-dismissing message acceptable
   * under SC 2.2.1; `danger` opts out of the timer entirely (`duration: null`).
   */
  useEffect(() => {
    if (duration === null || !open || paused) return undefined;
    const startedAt = Date.now();
    const t = setTimeout(() => dismissToast(id), remaining.current);
    return () => {
      clearTimeout(t);
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt));
    };
  }, [duration, open, paused, id]);

  if (!isPresent) return null;

  const Glyph = TONE_GLYPH[tone];
  const ink = toneInk[tone];
  const motionClass = phase === 'entering' ? MOTION.toast.enter : MOTION.toast.exit;

  return (
    <li
      /* rest FIRST, per the convention in Button.tsx — but note what that means here:
         everything below wins over a caller's version of it, and the mouse/focus
         handlers are deliberately in that group. They are what implements the SC 2.2.1
         pause, so they are not a caller's to replace. */
      {...rest}
      ref={ref}
      /* `pointer-events-auto` re-enables clicks that the region turns off — the
         viewport is a full-height fixed column, so leaving it clickable would swallow
         presses anywhere down the right-hand side of the page. */
      className={`tw-pointer-events-auto tw-relative tw-overflow-hidden tw-flex tw-items-start tw-gap-3 tw-p-3 tw-w-80 tw-max-w-[calc(100vw-2rem)] tw-bg-surface-raised tw-border tw-border-edge tw-rounded-srf tw-shadow-ds-lg ${motionClass}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      data-tone={tone}
      data-paused={paused ? 'true' : undefined}
      data-testid={`toast-${id}`}
    >
      <Glyph size={20} aria-hidden="true" className={`tw-shrink-0 ${ink}`} data-testid={`toast-${id}-icon`} />

      <div className="tw-flex-1 tw-min-w-0">
        <div className="tw-text-title-header tw-text-ink" data-testid={`toast-${id}-title`}>
          {title}
        </div>
        {description && (
          <div className="tw-mt-0.5 tw-text-caption-metadata tw-text-ink-secondary" data-testid={`toast-${id}-description`}>
            {description}
          </div>
        )}
        {action && (
          <button
            type="button"
            /* Acting on a toast dismisses it: the message has served its purpose and
               leaving it up invites a second click on something already done. */
            onClick={() => { action.onClick(); dismissToast(id); }}
            /* § 295 — the hover FADES like every other DS control: Toast was the one
               surface with no transition anywhere, so this link and the dismiss below
               snapped. The ink moves to `accent-hover` (Link's own hover) so there is a
               colour to tween — an underline appearing cannot fade. */
            className={`tw-mt-2 tw-text-title tw-text-accent hover:tw-text-accent-hover hover:tw-underline tw-rounded-ctl motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING}`}
            data-testid={`toast-${id}-action`}
          >
            {action.label}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => dismissToast(id)}
        /* 24px box round a 16px glyph. SC 2.5.8 asks for a 24px minimum target, and
           the reference kit's own close affordance was a 14px square. */
        className={`tw-shrink-0 tw-flex tw-items-center tw-justify-center tw-h-6 tw-w-6 tw-rounded-ctl tw-text-ink-tertiary hover:tw-text-ink hover:tw-bg-surface-hover-strong motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING}`}
        aria-label={dismissLabel}
        data-testid={`toast-${id}-dismiss`}
      >
        <Cross size={16} aria-hidden="true" />
      </button>

      {duration !== null && (
        /*
          The auto-dismiss made visible (2026-08-25): a hairline that drains over
          exactly the toast's duration. CSS carries the clock — `animation-duration`
          comes from the same `duration` the JS timeout uses, and hover/focus pause
          via `animation-play-state`, which freezes the bar precisely where the
          effect freezes `remaining` — the two cannot drift across pauses.

          `aria-hidden`: the announcers already carried the message; a draining bar
          is visual reassurance, not information a screen reader needs repeated.
          Under prefers-reduced-motion the animation is gated off and the hairline
          sits as a static accent edge. Sticky (danger) toasts render no bar at all —
          nothing is draining.
        */
        <div
          aria-hidden="true"
          className={`tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-0.5 ${toneBar[tone]} tw-origin-left motion-safe:tw-animate-toast-timer`}
          style={{
            animationDuration: `${duration}ms`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
          data-testid={`toast-${id}-timer`}
        />
      )}
    </li>
  );
});

export default Toast;
